import { getOrCreateDataLayer } from './helpers/dataLayer';
import { debounce } from './helpers/debounce';
import { EventParamAttributes } from './eventParamAttributes';
import { IN_VIEW_ATTRIBUTE, isInView } from './helpers/inView';
import {
  componentViewEvent,
  linkClickEvent,
  premiumStorefrontInViewEvent,
} from './eventPayloadDefinitions';
import { getCookie } from '../cookie-utils';
import { trackEvent } from './tracking';

type EventParamAttribute = keyof EventParamAttributes;

export type ReturnObjectTypes = {
  countryCode?: string | null;
  variant?: string | null;
  name?: string | null;
  position?: string | null;
  list?: string | null;
  id?: string | null;
  href?: string | null;
};

export const legacyTracking = ({
  analyticsRoot,
  resourceName,
  language,
  country,
  isEvergreen,
  eligibility,
  authenticated,
}: {
  analyticsRoot: HTMLElement;
  resourceName: string; // full name of resource, including category
  language: string;
  country: string;
  isEvergreen: boolean;
  eligibility: string;
  authenticated: boolean;
}) => {
  const dataLayer = getOrCreateDataLayer();
  const pageUrl = window.location.href;

  /**
   * Checks whether all of the keys of the supplied object
   * have values other than null.
   */
  function allAttributesPopulated(object: ReturnObjectTypes) {
    const values = Object.keys(object).map(
      e => object[e as keyof ReturnObjectTypes],
    );
    return !values.some(x => x === null);
  }

  /**
   * Retrieves an attribute from an element, and sets a corresponding
   * property on a supplied object, if that property is currently
   * set to a falsy value.
   */
  function populateAttribute(
    currentElement: HTMLElement,
    returnObject: ReturnObjectTypes,
    attribute: keyof ReturnObjectTypes,
    htmlAttribute: EventParamAttribute,
  ) {
    const attributeValue = currentElement.getAttribute(htmlAttribute);

    if (attributeValue && !returnObject[attribute]) {
      returnObject[attribute] = attributeValue;
    }
  }

  /**
   * Builds a promotion object to be used inside a GA event based on
   * attributes of a given element and its parents.
   */
  function buildPromotionObject(element: HTMLElement, clickEvent: boolean) {
    let currentElement = element;

    const returnObject: ReturnObjectTypes = {
      countryCode: country,
      variant: null,
      name: null,
      position: null,
      list: null,
      id: null,
    };

    if (clickEvent) {
      returnObject.href = null;
    }

    do {
      populateAttribute(
        currentElement,
        returnObject,
        'variant',
        'data-event-campaign-id',
      );
      populateAttribute(
        currentElement,
        returnObject,
        'id',
        'data-event-offer-type-id',
      );
      populateAttribute(
        currentElement,
        returnObject,
        'name',
        'data-component-name',
      );
      populateAttribute(
        currentElement,
        returnObject,
        'list',
        'data-component-name',
      );
      populateAttribute(
        currentElement,
        returnObject,
        'position',
        'data-component-position',
      );

      if (clickEvent && currentElement.hasAttribute('href')) {
        returnObject.href = currentElement.getAttribute('href');
      }

      if (
        allAttributesPopulated(returnObject) ||
        currentElement === analyticsRoot
      ) {
        break;
      }

      if (currentElement.parentElement) {
        currentElement = currentElement.parentElement;
      }
    } while (currentElement.parentElement);

    return returnObject;
  }

  /**
   * Builds a promotion object from an element if possible, otherwise
   * returns null.
   */
  function parseEvent(element: HTMLElement, clickEvent: boolean) {
    if (!element) return null;
    try {
      return buildPromotionObject(element, clickEvent);
    } catch (e) {
      return null;
    }
  }

  function pushPromoView(event: ReturnObjectTypes | null) {
    dataLayer.push({
      event: 'eec.promotionView',
      ecommerce: {
        promoView: {
          promotions: [event],
        },
      },
    });
  }

  function pushProductView(event: ReturnObjectTypes | null) {
    dataLayer.push({
      event: 'eec.impressionView',
      ecommerce: {
        impressions: [event],
      },
    });
  }

  const seenViewElements: Array<HTMLElement> = [];

  function pushStorefrontLoad(element: HTMLElement) {
    dataLayer.push({
      event: 'plan-sensitive-storefront-load',
      error: element.getAttribute('data-storefront-error') ? true : false,
      'current-plan': element.getAttribute('data-current-plan'),
    });
  }

  const getOrderedPlanOfferTypeIds = (): Array<string> => {
    const storefrontPlans = document.querySelectorAll(
      '[data-current-plan-text]',
    );
    const offerTypeIds: string[] = [];

    storefrontPlans.forEach(element => {
      const offerTypeId = element.getAttribute('data-event-offer-type-id');
      if (offerTypeId) {
        offerTypeIds.push(offerTypeId);
      }
    });
    return offerTypeIds;
  };

  function handleViewEvent(element: HTMLElement) {
    if (seenViewElements.indexOf(element) === -1 && isInView(element)) {
      seenViewElements.push(element);
      const parsedEvent = parseEvent(element, false);

      const component_name = element.getAttribute('data-component-name');
      const offerTypeId = element.getAttribute('data-event-offer-type-id');

      if (component_name === 'storefront') {
        const payload = {
          country: country ?? 'unknown',
          locale: language ?? 'unknown',
          full_url: pageUrl,
          timestamp: Math.floor(Date.now() / 1000),
          sorting_order: getOrderedPlanOfferTypeIds(),
          correlation_id: getCookie('sp_t') ?? 'unknown',
          is_evergreen: isEvergreen,
          eligibility,
        };

        trackEvent(
          premiumStorefrontInViewEvent(payload, {
            authorize: authenticated,
          }),
        );

        pushStorefrontLoad(element);
      } else if (component_name === 'storefront-plan') {
        const payload = {
          resource_name: resourceName,
          component_name,
          country: country ?? 'unknown',
          locale: language ?? 'unknown',
          full_url: pageUrl,
          correlation_id: getCookie('sp_t') ?? 'unknown',
          is_evergreen: isEvergreen,
          eligibility,
          offer_type_id: offerTypeId,
        };

        trackEvent(
          componentViewEvent(payload, {
            authorize: authenticated,
          }),
        );

        pushProductView(parsedEvent);
      } else {
        const payload = {
          resource_name: resourceName,
          component_name,
          country: country ?? 'unknown',
          locale: language ?? 'unknown',
          full_url: pageUrl,
          correlation_id: getCookie('sp_t') ?? 'unknown',
          is_evergreen: isEvergreen,
          eligibility,
        };

        trackEvent(
          componentViewEvent(payload, {
            authorize: authenticated,
          }),
        );

        pushPromoView(parsedEvent);
      }
    }
  }

  function trackInViewElements() {
    analyticsRoot
      .querySelectorAll<HTMLElement>(`[${IN_VIEW_ATTRIBUTE}]`)
      .forEach(element => {
        handleViewEvent(element);
      });
  }

  function pushPromoClick(event: ReturnObjectTypes | null) {
    dataLayer.push({
      event: 'eec.promotionClick',
      ecommerce: {
        promoClick: {
          promotions: [event],
        },
      },
    });
  }

  function pushProductClick(event: ReturnObjectTypes | null) {
    dataLayer.push({
      event: 'eec.productClick',
      ecommerce: {
        click: {
          actionField: { list: event?.list },
          products: [event],
        },
      },
    });
  }

  function getComponentType(element: HTMLElement) {
    return element
      .closest('[data-component-type]')
      ?.getAttribute('data-component-type');
  }

  function handleClickEvent(element: HTMLElement, link_url: string) {
    const parsedEvent = parseEvent(element, true);

    const payload = {
      resource_name: resourceName,
      link_url,
      country: country ?? 'unknown',
      locale: language ?? 'unknown',
      full_url: pageUrl,
      timestamp: Math.floor(Date.now() / 1000),
      correlation_id: getCookie('sp_t') ?? 'unknown',
      is_evergreen: isEvergreen,
      eligibility: eligibility,
      component_view:
        element
          .closest('[data-component-name]')
          ?.getAttribute('data-component-name') || null,
    };

    trackEvent(
      linkClickEvent(payload, {
        authorize: authenticated,
      }),
    );

    const componentType = getComponentType(element);

    if (componentType === 'product') {
      pushProductClick(parsedEvent);
    }

    // only track clicks on promos (hero) that don't go to the plans section as promo click events
    if (componentType === 'promo' && parsedEvent?.href !== '#plans') {
      pushPromoClick(parsedEvent);
    }
  }

  function setupPromotionTracking() {
    if (!analyticsRoot) {
      return;
    }

    trackInViewElements();

    window.addEventListener('resize', debounce(trackInViewElements, 500));
    window.addEventListener('scroll', debounce(trackInViewElements, 500));
    window.addEventListener(
      'orientationchange',
      debounce(trackInViewElements, 500),
    );

    analyticsRoot.addEventListener(
      'click',
      e => {
        if (e.target instanceof HTMLElement && e.target.closest('a[href]')) {
          handleClickEvent(
            e.target,
            (e.target
              .closest('a[href]')
              ?.getAttribute('href') as unknown as string) ?? '',
          );
        }
      },
      { capture: true },
    );
  }

  setupPromotionTracking();
};
